// @flow

// The TaskManager component manages which tasks to show when.

import _ from "lodash";
import React, { Component } from "react";
import { compose } from "react-apollo";

import type { TaskType, RetrieveTasksType } from "../../data/tasks";
import type { Result, Results, createResultType } from "../../data/results";
import type { retrieveProjectQueryResult } from "../../data/project";
import type { withAzureAuthType } from "../../data/azure";

import { withRetrieveProject } from "../../data/project";
import { withRetrieveTasks } from "../../data/tasks";
import { withCreateResult } from "../../data/results";
import { withAzureAuth } from "../../data/azure";

import Task from "../Task/Task";

import "./TaskManager.css";

type Props = {
  userId: string,
  projectId: string,
  sessionId: string,
  createResult: createResultType,
  azureAuth: withAzureAuthType,
  retrieveTasks: RetrieveTasksType,
  onTaskAdvance: (results: Results) => void
} & retrieveProjectQueryResult;

type State = {
  currentTask: ?TaskType,
  queue: Array<TaskType>,
  azureToken: string
};

class TaskManager extends Component<Props, State> {
  _mounted: boolean;

  constructor(props: Props) {
    super(props);
    this.state = {
      currentTask: null,
      queue: [],
      azureToken: ""
    };
    this._mounted = false;
  }

  componentWillMount() {
    this._mounted = true;

    this.fetchTasks();

    this.props.azureAuth().then(res => {
      if (res.data && res.data.azureAuth.token) {
        const azureToken = res.data.azureAuth.token;
        this.setState({ azureToken });
      } else {
        console.error("Could not retrieve Azure token");
        console.error(res);
      }
    });
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  fetchTasks() {
    console.log("Fetching tasks");
    this.props.retrieveTasks().then(res => {
      if (res.data && res.data.retrieveTasks && res.data.retrieveTasks.tasks) {
        const tasks = res.data.retrieveTasks.tasks;
        console.log("Got tasks: ", tasks);
        if (!this._mounted) {
          return;
        }
        if (this.state.currentTask === null) {
          this.setState({ currentTask: tasks[0], queue: tasks.slice(1) });
        } else {
          this.setState({ queue: this.state.queue.concat(tasks.slice(0)) });
        }
      }
    });
  }

  handleAdvance = (results: Results) => {
    const queue = this.state.queue;

    if (queue.length > 0) {
      this.setState({
        currentTask: queue[0],
        queue: queue.slice(1)
      });
    } else {
      this.setState({
        currentTask: null
      });
    }
    this.props.onTaskAdvance(results);
  };

  handleUploadResults = (results: Results) => {
    this.fetchTasks();
    console.log("Submitting results", results);
    const task = this.state.currentTask;
    if (!task) {
      throw new Error(
        `Got results ${JSON.stringify(results)}, but current task is null!`
      );
    }
    if (!results || results.toArray().length === 0) {
      throw new Error(`Got submission without results!`);
    }
    if (!this.props.sessionId) {
      throw new Error(`Got results and submission without SessionId`);
    }
    const getQueryParams = (result: Result) => ({
      variables: {
        kind: result.kind,
        secondsElapsed: _.round(result.secondsElapsed, 5),
        secondsLimit: _.round(result.secondsLimit, 5),
        confidence: result.kind === "response" ? result.confidence : null,
        lostFocus: result.lostFocus,
        searchesIds: result.searchesIds,
        taskId: task.id,
        userId: this.props.userId,
        sessionId: this.props.sessionId
      }
    });
    const allQueryParams = results.toArray().map(getQueryParams);

    const promises = allQueryParams.map(p => this.props.createResult(p));
    Promise.all(promises).then(responses => {
      console.log("Submitted, got responses:", responses);
    });
  };

  render() {
    const task = this.state.currentTask;
    const project = this.props.projectData.Project;

    return (
      <div className="TaskManager">
        {task && project ? (
          <Task
            key={task.id}
            task={task}
            uploadResults={this.handleUploadResults}
            advance={this.handleAdvance}
            azureToken={this.state.azureToken}
          />
        ) : (
          <p>Tasks loading...</p>
        )}
      </div>
    );
  }
}

const extendComponent = compose(
  withRetrieveProject,
  withCreateResult,
  withAzureAuth,
  withRetrieveTasks
);

export default extendComponent(TaskManager);



// WEBPACK FOOTER //
// ./src/components/TaskManager/TaskManager.js